/*
SPAStorageAdapter - Client-side storage for optimistic updates with server sync

## Mental Model

This adapter manages client-side state for a single aggregate, supporting
optimistic updates (unconfirmed actions) that are later confirmed by the server.

Architecture:
  - Server is the source of truth for confirmed actions
  - Each browser tab maintains its own in-memory projection of the aggregate
  - IndexedDB stores unconfirmed actions for durability (survives page refresh)
  - WebSocket broadcasts confirmed actions to all tabs

State flow:
  1. User performs action → written to IndexedDB + applied to in-memory state
  2. Action sent to server via WebSocket
  3. Server confirms → broadcasts to ALL connected tabs via WebSocket
  4. Each tab updates its in-memory state and removes action from IndexedDB

## Multi-Tab Behavior

Multiple browser tabs can be open simultaneously. Each tab:
  - Has its own SPAStorageAdapter instance with its own in-memory state
  - Shares the same IndexedDB database
  - Has its own WebSocket connection to the server

Key insight: Tabs don't need to coordinate with each other directly. They
coordinate implicitly through:
  1. IndexedDB - for unconfirmed action durability
  2. Server broadcasts - for confirmed action synchronization

When a tab processes a confirmed action (_performConfirmedActionInternal), it
re-reads ALL unconfirmed actions from IndexedDB and reapplies them. This causes
tabs to naturally "adopt" unconfirmed actions created by other tabs.

Example:
  - Tab A creates unconfirmed action X → writes to IndexedDB
  - Tab B doesn't see X yet (its in-memory state is stale)
  - Server confirms any action Z → broadcasts to both tabs
  - Both tabs call _performConfirmedActionInternal, re-read from IndexedDB
  - Both tabs now see and reapply X → tabs have converged

## Correctness Properties

Invariants maintained:
  1. lastConfirmedAgg.version equals the version of the last confirmed action
  2. currentUnconfirmedAgg = lastConfirmedAgg + all unconfirmed actions applied
  3. Unconfirmed actions in IndexedDB are a superset of what's needed (other
     tabs may have already gotten confirmation and deleted some)

Why the implementation is correct:
  - Confirmed actions are distributed via server broadcast, ensuring all tabs
    receive the same confirmations in the same order
  - The confirmActionQueue serializes confirmed action processing within a tab
  - Re-reading from IndexedDB on each confirmation ensures tabs converge
  - Deleting from IndexedDB by action ID is idempotent (safe if multiple tabs
    try to delete the same action)
  - Multiple tabs submitting the same unconfirmed action is harmless (server
    deduplicates by action ID)

Ordering guarantee:
  - Unconfirmed actions are sorted by (createdAt, actionId) to ensure
    deterministic ordering across tabs, even if timestamps collide

## Storage

We snapshot every 100 actions (to aid in getting a previous version of the
aggregate, which is used to undo); those snapshots live in memory only.

IndexedDB holds three tables: the unconfirmed actions (unconfirmedActions2),
a per-round cache of the last confirmed aggregate (roundAggregateCache — the
iPad offline cache, also used for online cache-first loads), and a metadata
singleton (schema/profile/owner stamps used to decide when to wipe). Wipe
scope depends on cause: a schema change clears only the rebuildable
aggregate cache; only an owner change (or unknown ownership) also clears the
queued unconfirmed actions, which are unsynced user work.

Actions for an aggregate must start at 0 and have no gaps. If we detect a gap,
that means that we missed a message from the server and need to download the
missing actions.
*/

import type {
  IncomingActionMessage,
  IncomingUnconfirmedActionMessage,
  Reducer,
} from "@engine/core";
import type { ActionError, ClientStorageAdapter } from "@engine/network";
import { type NumberOrRange } from "@engine/network";
import { removedAction, TIMESTAMPED_ACTION_TYPES } from "@engine/round/actions";
import upcastAction from "@engine/round/upcast";
import type { RoundAggregate } from "@engine/round/aggregate";
import {
  type ClientSideEffectWriter,
  type InfoStoreReader,
  type SideEffect,
} from "@engine/round/infoStore";
import { type Track } from "@lib/types";
import { anonymizeAggregate } from "@lib/utils/anonymization";
import { captureMessage } from "@lib/telemetry/sentry";
import { roundAggregateSchemaHash as schemaHash } from "@lib/roundSchemaHash";
import Dexie, { type EntityTable } from "dexie";
import { normalizeRoundDownload, type RoundDownload } from "./downloadedRound";
import {
  type CacheReadMissReason,
  readCachedAggregate,
  readCachedAggregateDetailed,
  writeCachedAggregate,
  type RoundAggregateCacheKey,
  type RoundAggregateCacheTable,
} from "./roundAggregateCache";

type UnconfirmedActionRecord = {
  aggregateId: string;
  actionId: string;
  actionMessage: IncomingUnconfirmedActionMessage;
  createdAt: number;
};

export type RoundLoadMode = "cache_first" | "downloaded" | "offline_cache";

export type Db = Dexie & {
  metadata: EntityTable<{
    id: "singleton";
    userProfileHash: string;
    schemaHash: string;
    // The underlying (logged-in) user this device's cached aggregates + queued
    // unconfirmed actions belong to. Used to wipe them when the account changes
    // without a sign-out. Optional for back-compat with rows written before
    // this field existed. Non-indexed, so no Dexie version bump needed.
    ownerUserId?: string;
  }>;
  unconfirmedActions2: EntityTable<UnconfirmedActionRecord>;
  roundAggregateCache: RoundAggregateCacheTable;
};

// Exported for tests that inspect the cache tables directly.
export function getDb(): Db {
  const db = new Dexie("SPAStorage") as Db;

  db.version(4).stores({
    metadata: "[id]",
    unconfirmedActions2:
      "[aggregateId+actionId], [aggregateId+version], [aggregateId]",

    // Old tables which we want Dexie to delete https://github.com/dexie/Dexie.js/issues/275
    unconfirmedActions: null,
    confirmedActions: null,
  });

  // v5: cache of the last confirmed aggregate, so a previously-loaded round
  // can open offline. See roundAggregateCache.ts.
  db.version(5).stores({
    roundAggregateCache: "aggregateId",
  });

  return db;
}

// Persist any deferred stand-in writes to their durable native files and wait
// for that to land, THROWING if the write cannot be made durable. Set by
// `installLockdownStandInStorage`; a no-op everywhere else, where writes are
// already durable in real IndexedDB. `init` uses it to make the proposal-text
// clear durable before it advances the owner stamp — and lets it throw, so a
// failed clear refuses to advance the stamp instead of stranding old text.
let flushDurableSnapshots: () => Promise<void> = async () => {};

export function setDurableSnapshotFlush(fn: () => Promise<void>): void {
  flushDurableSnapshots = fn;
}

// Utility to guarantee a strictly increasing timestamp within a single tab.
// Note: This is per-tab, so different tabs may generate the same timestamp.
// We handle this by using (createdAt, actionId) as the sort key - see
// sortUnconfirmedActions below.
let _lastMonotonicTimestamp = 0;

function getMonotonicTimestamp(): number {
  const now = Date.now();

  if (now <= _lastMonotonicTimestamp) {
    // Bump by 1 ms to preserve order
    _lastMonotonicTimestamp += 1;
    return _lastMonotonicTimestamp;
  }

  _lastMonotonicTimestamp = now;
  return now;
}

// Sort unconfirmed actions deterministically by (createdAt, actionId).
// The secondary sort by actionId ensures consistent ordering across tabs even
// when timestamps collide (which can happen since getMonotonicTimestamp is
// per-tab).
function sortUnconfirmedActions(
  actions: UnconfirmedActionRecord[],
): UnconfirmedActionRecord[] {
  return actions.sort((a, b) => {
    if (a.createdAt !== b.createdAt) {
      return a.createdAt - b.createdAt;
    }

    return a.actionId.localeCompare(b.actionId);
  });
}

/**
 * Configures the offline aggregate cache for an adapter instance. Only the iPad
 * Capacitor shell passes `enabled: true`; everywhere else the cache is inert.
 * `isOffline` is injectable for tests; it defaults to a `navigator.onLine`
 * reader so the cache is only served when the device genuinely reports offline.
 */
export type OfflineCacheConfig = {
  // A function is re-evaluated during `init` (latest at adapter construction,
  // which runs AFTER the aggregate download). This lets a round that only
  // qualifies for caching *because of* the download — the learned-size
  // cache-first path records the payload size mid-download — persist on that
  // same first load, instead of waiting for the next visit's download.
  enabled: boolean | (() => boolean);
  isOffline?: () => boolean;
  // When the failed download never produced a server response (backend
  // unreachable despite connectivity), the cache may serve as if offline.
  // Server-sent errors must return false here so they surface. SP-2612.
  isUnreachableError?: (error: unknown) => boolean;
  // When set (and online), `init` tries the IndexedDB cache FIRST instead of
  // downloading: if a valid cached aggregate is no more than `maxActionGap`
  // actions behind the server's current version, it's used as the initial
  // confirmed aggregate and the socket `register()` handshake streams the small
  // gap — skipping the full aggregate download. If the cache is missing, stale
  // beyond the gap, ahead of the server, or the current version can't be
  // determined, `init` downloads fresh as usual. `enabled` (cache writes) must
  // also be true so the cache populates on fresh loads.
  cacheFirst?: {
    getCurrentVersion: (aggregateId: string) => Promise<number | null>;
    /**
     * The server's current log revision (from the bootstrap). When this is
     * configured, cache-first requires it: a cached row from a different
     * revision, from before rows carried one, or when the server could not
     * say (null) is a miss even at the same version — an in-place log edit
     * changes content without moving the version, and the version gate alone
     * cannot see it. Leave it unconfigured only where no server revision can
     * exist (tests, tooling).
     */
    getCurrentLogRevision?: () => string | null;
    maxActionGap: number;
  };
  // Desktop/web only (SP-2823). While this tab is hidden, the cache persist
  // that follows each confirmed action is DEFERRED instead of written: every
  // open tab of a round applies every confirmation, and with dozens of tabs
  // the resulting multi-MB writes to the one shared IndexedDB row contend
  // with the confirmation path itself. A visible tab keeps the row current;
  // a hidden tab writes its latest confirmed state once, when it becomes
  // visible again. Fresh-download writes and explicit flushes (sign-out) are
  // never deferred. Leave unset on the iPad shell — a backgrounded app has no
  // other tab to write for it, and its cache is what offline use runs on.
  // Trade-off: while EVERY tab of a round is hidden, the cached snapshot
  // lags the server until one is shown again (or the round is reopened —
  // the cache-first gate is version-based and catches up over the socket).
  visibility?: TabVisibility;
};

/** How the adapter learns whether its tab is hidden (see `OfflineCacheConfig.visibility`). */
export type TabVisibility = {
  isHidden: () => boolean;
  // Subscribes to visibility changes; returns the unsubscribe.
  onChange: (listener: () => void) => () => void;
};

/** The browser tab's visibility, via the Page Visibility API. */
export const documentTabVisibility: TabVisibility = {
  isHidden: () =>
    typeof document !== "undefined" && document.visibilityState === "hidden",
  onChange: (listener) => {
    if (typeof document === "undefined") {
      return () => {};
    }

    document.addEventListener("visibilitychange", listener);
    return () => document.removeEventListener("visibilitychange", listener);
  },
};

/**
 * Why a cache-first attempt fell through to the download path. The cache-read
 * reasons come from readCachedAggregateDetailed; the rest are the version
 * gate. Reported on the round-open RUM signal so field misses are
 * attributable — and `gap` (when known) is the number that says whether
 * CACHE_FIRST_MAX_ACTION_GAP is tuned right.
 */
export type CacheFirstMissReason =
  | CacheReadMissReason
  | "version_unknown"
  | "cache_ahead"
  | "gap_too_large"
  // The server's log revision differs from the one the row was built under
  // (an in-place log edit since the row was cached). Same version, different
  // content: re-download.
  | "log_revision_changed"
  // The row carries no server revision (written before rows recorded one,
  // or downloaded without the header) and the server now reports one: it
  // cannot be verified against an in-place edit, so re-download once.
  | "legacy_row"
  // The server could not say which log revision it serves (fence unreadable,
  // or the aggregate was built across a destructive edit). Unknown is not
  // "skip the check": a same-version in-place edit would be invisible to the
  // version gate and the socket has nothing to repair, so re-download.
  | "log_revision_unknown"
  // The DB-level wipe (validateDb) emptied the cache before the read: a new
  // account on the device. Attributed at init, not in the read (the row is
  // already gone by then).
  | "account_changed";

/**
 * Try to satisfy a cache-first load from the IndexedDB cache. Returns the cached
 * aggregate when it's valid for this session AND within `maxActionGap` actions
 * of the server's current version (and not ahead of it). Returns a null
 * aggregate — meaning "download fresh" — with the miss reason on a cache miss,
 * an unknown current version, or a gap that is too large or negative. `gap` is
 * reported whenever both versions were known: on hits AND on version-gate
 * misses.
 */
async function tryCacheFirstAggregate(
  table: RoundAggregateCacheTable,
  key: RoundAggregateCacheKey,
  cacheFirst: NonNullable<OfflineCacheConfig["cacheFirst"]>,
): Promise<{
  aggregate: RoundAggregate | null;
  logRevision: string | null;
  gap: number | null;
  missReason: CacheFirstMissReason | null;
}> {
  const { record: cached, missReason } = await readCachedAggregateDetailed(
    table,
    key,
  );

  if (cached == null) {
    return { aggregate: null, logRevision: null, gap: null, missReason };
  }

  const currentVersion = await cacheFirst
    .getCurrentVersion(key.aggregateId)
    .catch(() => null);

  if (currentVersion == null) {
    return {
      aggregate: null,
      logRevision: null,
      gap: null,
      missReason: "version_unknown",
    };
  }

  const gap = currentVersion - cached.version;

  // gap < 0: cache is somehow ahead of the server (e.g. a server-side reset) —
  // don't serve actions the server no longer has. gap > max: too far behind to
  // catch up cheaply over the socket.
  if (gap < 0) {
    return {
      aggregate: null,
      logRevision: null,
      gap,
      missReason: "cache_ahead",
    };
  }

  if (gap > cacheFirst.maxActionGap) {
    return {
      aggregate: null,
      logRevision: null,
      gap,
      missReason: "gap_too_large",
    };
  }

  if (cacheFirst.getCurrentLogRevision != null) {
    const serverRevision = cacheFirst.getCurrentLogRevision() ?? null;

    if (serverRevision == null) {
      return {
        aggregate: null,
        logRevision: null,
        gap,
        missReason: "log_revision_unknown",
      };
    }

    const rowRevision = cached.logRevision ?? null;

    if (rowRevision == null) {
      return {
        aggregate: null,
        logRevision: null,
        gap,
        missReason: "legacy_row",
      };
    }

    if (rowRevision !== serverRevision) {
      return {
        aggregate: null,
        logRevision: null,
        gap,
        missReason: "log_revision_changed",
      };
    }
  }

  return {
    aggregate: cached.aggregate,
    logRevision: cached.logRevision ?? null,
    gap,
    missReason: null,
  };
}

/** Default offline detector: true only when the browser reports it is offline. */
function deviceIsOffline(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

export class SPAStorageAdapter implements ClientStorageAdapter {
  aggregateId: string;
  userProfileHash: string;

  // Used to anonymize the aggregate after confirmed actions.
  viewerTrack: Track;
  // The effective viewer's user id, used alongside viewerTrack to decide
  // whether hidden recommenders' notes get deleted (restricted funder view)
  // when re-anonymizing after a confirmed action.
  viewerUserId: string | null;

  // True when the confirmed aggregate was served from the offline cache
  // because the server download failed (device offline or server error).
  readonly loadedFromCache: boolean;

  // Which of the three load paths served this open. Reported by the CALLER, not
  // here: the caller's window is the one the funder actually waits through
  // (bootstrap and profile requests before init, field responses after), so a
  // timer started inside init would exclude most of it. `loadedFromCache` can't
  // substitute — it lumps the online cache-first fast path together with the
  // offline fallback, which mean opposite things.
  readonly loadMode: RoundLoadMode = "downloaded";

  // Cache-first attempt facts for the round-open signal (null when no attempt
  // ran — config off or device offline). `cacheFirstGap` is how many actions
  // behind the server the cached copy was, reported on hits and version-gate
  // misses alike, so the field distribution can say where the catch-up cap
  // should sit. `cacheFirstMissReason` names why an attempt fell through.
  readonly cacheFirstGap: number | null = null;
  readonly cacheFirstMissReason: CacheFirstMissReason | null = null;

  // Whether this client was eligible for the cache-first fast path at all. A
  // "downloaded" load with this false is expected; with it true the cache missed
  // or was too far behind, which is the interesting case.
  readonly cacheFirstEligible: boolean = false;

  /**
   * Edits still queued AFTER reconciliation, or null when the queue came out
   * empty. Reported by the caller alongside the round-open event, under the same
   * abandoned-load guard, so a superseded load can't claim a backlog for a round
   * the funder has already left.
   *
   * Post-reconciliation is the load-bearing part: an online load asks the server
   * which queued edits it already holds and deletes those, so counting the
   * pre-reconciliation array would report a backlog with an old age for a queue
   * that was just emptied — making every reconnect a false alarm.
   */
  readonly pendingEditReport: {
    queuedCount: number;
    oldestQueuedAgeMs: number;
  } | null = null;

  // Count of queued unconfirmed edits that couldn't be replayed onto the cached
  // aggregate during an offline load — kept in the queue (they still sync on
  // reconnect) but not reflected in this session's view. Surfaced in the
  // OfflineBanner so the funder isn't misled into redoing them. 0 normally.
  readonly unshownOfflineEditCount: number;

  // Gate for the offline aggregate cache. Only the iPad Capacitor shell enables
  // it; everywhere else (desktop/web, iPhone) this is false and the adapter
  // neither persists nor reads the cache, leaving behavior byte-identical to
  // the pre-cache implementation.
  private readonly offlineCacheEnabled: boolean;
  // Confirmed field edits applied WITHOUT their field-response write (both
  // versions absent on the server — see the confirmation gate). The
  // confirmed-snapshot rebuild replays these actions and must keep the
  // suppression, or the unauthoritative merge lands in the cache after all.
  private readonly fieldWriteSuppressedActionIds = new Set<string>();

  // Version of the aggregate this load downloaded from the server, or null when
  // it served the offline cache instead. Only the cache write that persists
  // exactly this version is writing what the server just handed us, which is
  // what lets it drop a stored row sitting ahead of it. See
  // `writeCachedAggregate`.
  private readonly downloadedVersion: number | null;

  currentUnconfirmedAgg: RoundAggregate;
  lastConfirmedAgg: RoundAggregate;

  confirmedActionsSinceLoad: IncomingActionMessage[] = [];
  // Ids of the actions above. The confirmed-action replay filters the queue
  // against this set so a stale row — a best-effort queue delete that failed —
  // can never be re-applied on top of an aggregate that already contains it.
  private confirmedActionIdsSinceLoad = new Set<string>();
  confirmedSnapshotsSinceLoad: RoundAggregate[] = [];

  // Write-through coalescing for the offline cache persist: `dirty` marks that
  // lastConfirmedAgg changed since the last write started; `promise` is the
  // in-flight drain (null when idle).
  private aggregatePersistDirty = false;
  private aggregatePersistPromise: Promise<void> | null = null;
  // Set when a confirmed-action persist was skipped because the tab was
  // hidden (see `OfflineCacheConfig.visibility`); cleared by the write that
  // lands once the tab is visible again. The visibility subscription is held
  // only while a persist is deferred, so a superseded adapter (round switch)
  // does not keep listening for the rest of the page's life.
  private aggregatePersistDeferred = false;
  private unsubscribeVisibility: (() => void) | null = null;
  // When `lastConfirmedAgg` was last replaced. Every cache write is stamped
  // with it (`cachedAt`), so a write that reaches the store late — a hidden
  // tab's deferred persist, an explicit flush — ranks by its snapshot's age
  // in the same-version tiebreak: a row another tab stored meanwhile (say a
  // fresh download of corrected content at the same version) is kept.
  private lastConfirmedAggAt = Date.now();
  private readonly visibility: TabVisibility | null;
  // Timing of the last confirmed-action apply and the last aggregate cache
  // persist, for the stall report (SP-2823): tells storage contention apart
  // from a starved tab. `endedAt` is null while the operation is in flight.
  private lastConfirmedApply: {
    startedAt: number;
    endedAt: number | null;
    version: number;
  } | null = null;
  private lastCachePersist: {
    startedAt: number;
    endedAt: number | null;
    version: number;
  } | null = null;
  // The server log revision this adapter's confirmed snapshots descend from
  // (from the download's header, or the cache row on a cached load); written
  // onto every cache row this adapter persists. Null when unknown.
  private readonly cacheLogRevision: string | null;

  db: Db;
  downloadActionsByVersion: (
    aggregateId: string,
    versions: NumberOrRange[],
  ) => Promise<{ [version: number]: IncomingActionMessage }>;
  infoStore: InfoStoreReader & ClientSideEffectWriter;

  // Queue to ensure confirmed actions are processed sequentially
  private confirmActionQueue: Promise<Error | ActionError[]> = Promise.resolve(
    [],
  );

  constructor(
    aggregateId: string,
    userProfileHash: string,
    lastConfirmedAgg: RoundAggregate,
    currentUnconfirmedAgg: RoundAggregate,
    db: Db,
    downloadSpecificActions: (
      aggregateId: string,
      versions: NumberOrRange[],
    ) => Promise<{ [version: number]: IncomingActionMessage }>,
    snapshotsSinceLoad: RoundAggregate[],
    infoStore: InfoStoreReader & ClientSideEffectWriter,
    viewerTrack: Track,
    viewerUserId: string | null = null,
    loadedFromCache: boolean = false,
    offlineCacheEnabled: boolean = false,
    unshownOfflineEditCount: number = 0,
    downloadedVersion: number | null = null,
    visibility: TabVisibility | null = null,
    cacheLogRevision: string | null = null,
  ) {
    this.visibility = visibility;
    this.cacheLogRevision = cacheLogRevision;
    this.aggregateId = aggregateId;
    this.userProfileHash = userProfileHash;
    this.currentUnconfirmedAgg = currentUnconfirmedAgg;
    this.lastConfirmedAgg = lastConfirmedAgg;
    this.db = db;
    this.downloadActionsByVersion = downloadSpecificActions;
    this.confirmedSnapshotsSinceLoad = snapshotsSinceLoad;
    this.infoStore = infoStore;
    this.viewerTrack = viewerTrack;
    this.viewerUserId = viewerUserId;
    this.loadedFromCache = loadedFromCache;
    this.offlineCacheEnabled = offlineCacheEnabled;
    this.unshownOfflineEditCount = unshownOfflineEditCount;
    this.downloadedVersion = downloadedVersion;

    // Persist the freshly-loaded aggregate to the offline cache without blocking
    // construction/render (iPad only). Skipped when we *loaded from* the cache —
    // re-writing the same snapshot would be wasted work. Routed through the same
    // coalescing scheduler as confirmed-action persists so an initial write and
    // an immediately-following action collapse into one trailing write.
    if (this.offlineCacheEnabled && !this.loadedFromCache) {
      this.scheduleAggregateCachePersist();
    }
  }

  static async init(
    reducer: Reducer,
    aggregateId: string,
    userProfileHash: string,
    downloadRound: (aggregateId: string) => Promise<RoundDownload>,
    downloadActionsById: (
      aggregateId: string,
      ids: string[],
    ) => Promise<{ [id: string]: IncomingActionMessage }>,
    downloadActionsByVersion: (
      aggregateId: string,
      versions: NumberOrRange[],
    ) => Promise<{ [version: number]: IncomingActionMessage }>,
    infoStore: InfoStoreReader & ClientSideEffectWriter,
    viewerTrack: Track = "NO_TRACK",
    viewerUserId: string | null = null,
    offlineCache: OfflineCacheConfig = { enabled: false },
    ownerUserId?: string,
  ): Promise<SPAStorageAdapter> {
    const db = getDb();
    // Resolved per call site: the value can legitimately change mid-init (the
    // learned-size record happens inside downloadRound), and the construction-
    // time evaluation below is what decides whether this load persists.
    const offlineCacheEnabled = () =>
      typeof offlineCache.enabled === "function"
        ? offlineCache.enabled()
        : offlineCache.enabled;
    const isOffline = offlineCache.isOffline ?? deviceIsOffline;
    const isUnreachableError = offlineCache.isUnreachableError ?? (() => false);

    // On an account/schema change, `validateDb` (below) clears the affected
    // SPAStorage tables (cache always; the queued-edit table only on an
    // owner change) and advances the stamps. But the proposal
    // text — rebuildable cache — lives in a SEPARATE
    // database (`ClientInfoStore`) — persisted to its own native file under
    // Lockdown Mode — that `validateDb` can't reach. Clear it, and make that
    // clear DURABLE, BEFORE the owner stamp advances: `validateDb` advances the
    // stamp last, so a kill in between leaves the stamp stale and the next
    // launch re-detects the change and re-clears. If we cleared after, a kill in
    // the window would leave the stamp on the new owner with the old text still
    // on disk, hydrated on the next launch. Detect read-only here so the common
    // (no-change) load skips all of it.
    // Owner-scoped like the queue itself: the sidecar is the replay
    // dependency of queued field edits, and its rows are content-hash-keyed
    // server data an aggregate schema move does not invalidate. Only an
    // owner change (or unknown ownership) clears it, for privacy.
    const pendingWipeReason = await peekWipeReason(db, ownerUserId);

    if (pendingWipeReason != null && pendingWipeReason !== "schema_changed") {
      await infoStore.clearFieldResponseCache?.();
      // Persist the (now empty) field-text file before the stamp advances. A
      // no-op off the stand-in, where the clear is already durable in IndexedDB.
      // This THROWS if the file cannot be rewritten (Lockdown FS denial): we
      // then leave `validateDb` unrun, so the owner stamp stays on the old
      // account and the next launch re-detects the change and retries — better
      // than advancing the stamp over text we failed to clear.
      await flushDurableSnapshots();
    }

    // Validate (and wipe on a schema/owner change) BEFORE reading the queue, so
    // an owner-change clear can't race the read below and replay the previous
    // account's actions. It's a single fast metadata transaction; the network
    // download still parallelizes with the queue read. The wipe reason feeds
    // the cache-first miss attribution below.
    const wipeReason = await validateDb(db, userProfileHash, ownerUserId);

    // One-time data migration: queued edits written by a bundle from before
    // payload timestamps existed lack payload.timestamp, which the server's
    // message schema now requires. Stamp them from the queue row's own
    // insertion time — deterministic, so however many times the edit is
    // resent it carries the same value and resubmit identity holds.
    await db.unconfirmedActions2
      .where("aggregateId")
      .equals(aggregateId)
      .modify((record) => {
        // Retired action types must be upcast before resend: the server's
        // message schema accepts only current actions, so a raw retired
        // payload would fail parsing and stay queued forever. Upcast before
        // timestamp stamping so the stamp rule sees the current type.
        const retired = removedAction.safeParse(record.actionMessage.payload);

        if (retired.success) {
          record.actionMessage.payload = upcastAction(retired.data);
        }

        // Deprecated types are deliberately not migrated: they have no
        // upcast by design, they parse on the wire, and a resend gets the
        // server's designed non-creatable rejection with the standard
        // user-visible failure.
        const payload = record.actionMessage.payload as {
          type: string;
          timestamp?: string;
        };

        if (
          TIMESTAMPED_ACTION_TYPES.has(payload.type) &&
          payload.timestamp == null
        ) {
          payload.timestamp = new Date(record.createdAt).toISOString();
        }
      });

    // Read the unconfirmed action queue in parallel with whichever acquisition
    // strategy runs below.
    const unconfirmedActionsPromise = db.unconfirmedActions2
      .where("aggregateId")
      .equals(aggregateId)
      .toArray();

    let updatedConfirmedAgg: RoundAggregate;
    let loadedFromCache = false;
    // The server log revision the served content descends from: the header
    // on a download, the row's own on a cached load. See
    // RoundAggregateCacheRecord.logRevision.
    let cacheLogRevision: string | null = null;
    // Which of the three load paths below actually served this open. Reported at
    // the end of init: `loadedFromCache` alone can't distinguish the online
    // cache-first fast path from the offline fallback, and those mean opposite
    // things (one is the optimization working, the other is degraded mode).
    let loadMode: RoundLoadMode = "downloaded";
    // Set only when this load downloads the round from the server. Taken from
    // the download rather than the cache-first bootstrap because cache WRITES
    // are gated by `offlineCache.enabled`, which is broader and evaluated later:
    // on the iPad it covers every round, and a round can become cache-first
    // mid-load (its size is learned inside `downloadRound`). Those loads have no
    // bootstrap version, and they are exactly the ones that must be able to drop
    // a stale row.
    let downloadedVersion: number | null = null;

    // Cache-first: when online and configured, reuse a recent cached aggregate
    // and skip the (multi-MB) download. Falls through to the download path on a
    // miss / too-stale cache / unknown current version.
    const cacheFirst = offlineCache.cacheFirst;
    const cacheFirstAttempt =
      cacheFirst && !isOffline()
        ? await tryCacheFirstAggregate(
            db.roundAggregateCache,
            {
              aggregateId,
              schemaHash,
              userProfileHash,
              viewerTrack,
              viewerUserId,
            },
            cacheFirst,
          )
        : null;
    const cacheFirstAgg = cacheFirstAttempt?.aggregate ?? null;

    if (cacheFirstAgg != null) {
      // Loaded from cache while online; the socket `register()` handshake will
      // stream the (small) action gap. Treated like the offline cache load for
      // unconfirmed-action replay below.
      updatedConfirmedAgg = cacheFirstAgg;
      loadedFromCache = true;
      loadMode = "cache_first";
      cacheLogRevision = cacheFirstAttempt?.logRevision ?? null;
    } else {
      let result = await downloadRound(aggregateId).then(
        (download) => ({
          ok: true as const,
          ...normalizeRoundDownload(download),
        }),
        (error: unknown) => ({ ok: false as const, error }),
      );

      // If the initial download failed but the device is online now, retry once.
      // Connectivity can return *during* the load (e.g. the round was opened
      // offline and Wi-Fi reconnected mid-fetch), leaving the initial
      // getAggregate promise rejected; without a fresh attempt the load would
      // fall through to the throw below and never use the (now reachable) server
      // or the cache. Retrying yields FRESH data when we're back online — and a
      // genuine online server error still surfaces (the retry fails and we
      // throw), so this doesn't mask errors behind a possibly-stale cache.
      if (!result.ok && offlineCacheEnabled() && !isOffline()) {
        result = await downloadRound(aggregateId).then(
          (download) => ({
            ok: true as const,
            ...normalizeRoundDownload(download),
          }),
          (error: unknown) => ({ ok: false as const, error }),
        );
      }

      if (result.ok) {
        updatedConfirmedAgg = result.aggregate;
        downloadedVersion = result.aggregate.version;
        cacheLogRevision = result.logRevision ?? null;
        // The freshly-downloaded aggregate is persisted to the cache by the
        // constructor's non-blocking scheduler. Writing it here with `await`
        // blocked the round from rendering until the multi-MB IndexedDB write
        // finished — a large, visible stall.
      } else if (
        offlineCacheEnabled() &&
        (isOffline() || isUnreachableError(result.error))
      ) {
        // Offline, or the backend is unreachable (no server response), on a
        // cache-enabled client: fall back to the cached aggregate from a
        // previous load, if it is still valid for this session. An error the
        // server actually sent must surface, not be masked by a possibly-stale
        // cached round.
        const cached = await readCachedAggregate(db.roundAggregateCache, {
          aggregateId,
          schemaHash,
          userProfileHash,
          viewerTrack,
          viewerUserId,
        });

        if (cached == null) {
          throw result.error;
        }

        updatedConfirmedAgg = cached.aggregate;
        loadedFromCache = true;
        loadMode = "offline_cache";
        cacheLogRevision = cached.logRevision ?? null;
      } else {
        // Cache disabled or online failure: surface the download error.
        throw result.error;
      }
    }

    // A reconnect or schema change may hydrate a snapshot without replaying
    // the deletion event. Reconcile its tombstones before reading queued edits
    // or serving cached answers, including the native Lockdown-mode sidecar.
    const deletionEffects: SideEffect[] = Object.values(
      updatedConfirmedAgg.proposalForms,
    )
      .flatMap((form) => Object.values(form.proposals))
      .filter((proposal) => proposal.applicationDeleted)
      .map((proposal) => ({
        type: "PURGE_APPLICATION_FIELD_RESPONSES",
        roundId: updatedConfirmedAgg.id,
        proposalId: proposal.id,
        fieldResponsesId: proposal.fieldResponsesId,
      }));

    if (deletionEffects.length > 0) {
      await infoStore.applySideEffects(deletionEffects);
      await flushDurableSnapshots();
    }

    const unconfirmedActions = await unconfirmedActionsPromise;

    let currentUnconfirmedAgg = updatedConfirmedAgg;
    let unshownOfflineEditCount = 0;
    let confirmedActions: { [id: string]: IncomingActionMessage } = {};

    if (unconfirmedActions.length > 0) {
      // When offline we can't ask the server which unconfirmed actions are
      // already confirmed; replay them all. Reconciliation on reconnect is
      // idempotent (confirmed actions delete their unconfirmed twin by id).
      // Every ONLINE load asks — including cache-first: its socket catch-up
      // only streams actions newer than the cached version, so a stale row
      // confirmed BEFORE the cache was written would otherwise be replayed
      // here and never reconciled by the catch-up.
      if (!isOffline()) {
        try {
          confirmedActions = await downloadActionsById(
            aggregateId,
            unconfirmedActions.map((action) => action.actionId),
          );
        } catch (error) {
          if (!loadedFromCache) {
            // The plain online load already required the network; keep
            // surfacing its failures unchanged.
            throw error;
          }
          // Cache-first: fall back to replaying everything (the pre-dedup
          // behavior); reconnect reconciliation heals any stale row.
        }
      }

      for (const action of sortUnconfirmedActions(unconfirmedActions)) {
        const appliedAction = confirmedActions[action.actionId];

        if (appliedAction) {
          // Best-effort: the adapter is seeded with this id below, so even if
          // the delete fails the replay filter still skips the stale row.
          try {
            await db.unconfirmedActions2
              .where("aggregateId")
              .equals(aggregateId)
              .and(
                (unconfirmedAction) =>
                  unconfirmedAction.actionId === action.actionId,
              )
              .delete();
          } catch {
            // Swept again by the confirmed-action replay filter.
          }

          continue;
        }

        // No complete before-version, no local apply: merging onto nothing
        // would persist only the edited field. The row stays queued and
        // unshown; the server adjudicates at sync.
        if (
          (await hydrateFieldResponseDependency(
            infoStore,
            action.actionMessage.payload,
          )) !== "ready"
        ) {
          captureMessage(
            `Skipping unconfirmed action ${action.actionId}: its before-version could not be hydrated`,
            { level: "warning" },
          );
          unshownOfflineEditCount += 1;
          continue;
        }

        const result = reducer.performAction(
          currentUnconfirmedAgg,
          action.actionMessage,
        );

        if (result instanceof Error) {
          // Keep the action queued (the server decides on reconnect) but
          // don't apply it locally. Never throw here: one unreplayable row
          // would make the round permanently unopenable, and the intended
          // recovery is the server's verdict at sync time.
          captureMessage(
            `Skipping unconfirmed action ${action.actionId} that failed to replay on the ${loadedFromCache ? "cached" : "downloaded"} aggregate: ${result.message}`,
            { level: "warning" },
          );
          unshownOfflineEditCount += 1;
          continue;
        }

        // Apply per action, like the confirmation replay: a later queued
        // edit may hydrate against the version this action's
        // WRITE_FIELD_RESPONSES produces (chained unsynced edits whose
        // intermediate versions exist nowhere else), so the effects must
        // land in the store before the next iteration's hydration runs.
        await applySideEffectsBestEffort(infoStore, result.sideEffects.effects);
        currentUnconfirmedAgg = result.agg;
      }
    }

    // Only the rows reconciliation LEFT behind — see pendingEditReport.
    const stillPending = unconfirmedActions.filter(
      (action) => confirmedActions[action.actionId] == null,
    );
    const pendingEditReport =
      stillPending.length > 0
        ? {
            queuedCount: stillPending.length,
            // createdAt is wall-clock based (getMonotonicTimestamp), so unlike the
            // page-scoped confirmation clock this age survives a relaunch — which
            // is exactly the stranded-edit case worth catching.
            oldestQueuedAgeMs: Math.max(
              0,
              Date.now() -
                Math.min(...stillPending.map((action) => action.createdAt)),
            ),
          }
        : null;

    const adapter = new SPAStorageAdapter(
      aggregateId,
      userProfileHash,
      updatedConfirmedAgg,
      currentUnconfirmedAgg,
      db,
      downloadActionsByVersion,
      [updatedConfirmedAgg],
      infoStore,
      viewerTrack,
      viewerUserId,
      loadedFromCache,
      offlineCacheEnabled(),
      unshownOfflineEditCount,
      downloadedVersion,
      offlineCache.visibility ?? null,
      cacheLogRevision,
    );
    (
      adapter as { pendingEditReport: typeof pendingEditReport }
    ).pendingEditReport = pendingEditReport;
    // Assigned rather than passed: the constructor's positional list is already
    // long, and these are read-only reporting facts, not load inputs.
    (adapter as { loadMode: RoundLoadMode }).loadMode = loadMode;
    (adapter as { cacheFirstEligible: boolean }).cacheFirstEligible =
      offlineCache.cacheFirst != null;
    (adapter as { cacheFirstGap: number | null }).cacheFirstGap =
      cacheFirstAttempt?.gap ?? null;
    // A "no_row" miss right after the DB-level wipe is the wipe's doing —
    // validateDb clears the cache BEFORE the read, so the per-read schema
    // check can never see the row it would have rejected. Re-attribute, or a
    // deploy-wide invalidation reads as a fleet of fresh devices. A fresh DB
    // stays "no_row": nothing existed to wipe.
    const missReason =
      cacheFirstAttempt?.missReason === "no_row" &&
      (wipeReason === "schema_changed" || wipeReason === "account_changed")
        ? wipeReason
        : (cacheFirstAttempt?.missReason ?? null);
    (
      adapter as { cacheFirstMissReason: CacheFirstMissReason | null }
    ).cacheFirstMissReason = missReason;

    // Ids the server just told us are already confirmed. Seeding the replay
    // filter with them means a failed dedup-delete above cannot lead to the
    // stale row being re-applied by a later confirmation this session.
    for (const id of Object.keys(confirmedActions)) {
      adapter.confirmedActionIdsSinceLoad.add(id);
    }

    return adapter;
  }

  async performUnconfirmedAction(
    reducer: Reducer,
    actionMessage: IncomingUnconfirmedActionMessage,
  ): Promise<null | Error> {
    const result = reducer.performAction(
      this.currentUnconfirmedAgg,
      actionMessage,
    );

    if (result instanceof Error) {
      return result;
    }

    // One path for every platform: queue the edit BEFORE applying its side
    // effects. `applySideEffects` rewrites proposal text in `ClientInfoStore`
    // and notifies subscribers, so running it first would leave the UI showing
    // an edit whose queue write then failed — and unlike the aggregate those
    // writes have no undo, so the aggregate and the proposal text would
    // diverge (the UI shows text the server never receives, and the next
    // field edit references a version the server does not have). Queue-first
    // makes a failed write atomic: nothing has been applied yet. It also
    // matches replay-on-launch, where the queued action regenerates the side
    // effects. (Historically the reorder was Lockdown-only — gated by a
    // usingStandInStorage flag — with the web keeping side-effects-first; that
    // was safe only while queue writes could not fail. Now that runtime
    // IndexedDB failures are handled, the reorder is what makes the failure
    // handling correct, so both paths share it.)
    //
    // Failures surface per the ClientStorageAdapter contract — as a returned
    // Error, NOT a throw. `Client`'s action pump has a last-resort catch, but
    // the contract keeps the error attributable to this one edit.
    const previousUnconfirmedAgg = this.currentUnconfirmedAgg;
    this.currentUnconfirmedAgg = result.agg;

    try {
      await this.db.unconfirmedActions2.put({
        createdAt: getMonotonicTimestamp(),
        aggregateId: this.aggregateId,
        actionId: actionMessage.id,
        actionMessage,
      });
    } catch (error) {
      // A queued edit that cannot be stored has not happened. Undo the
      // in-memory advance and report it through the ClientStorageAdapter
      // contract — as a returned Error, NOT a throw. `Client`'s action pump
      // awaits this with no rejection handler, so throwing would leave
      // `isActionPending` stuck true and jam every later edit, undo/redo and
      // server confirmation for the rest of the session.
      this.currentUnconfirmedAgg = previousUnconfirmedAgg;
      return error instanceof Error ? error : new Error(String(error));
    }

    try {
      // Deliberately NOT best-effort (see applySideEffectsBestEffort): a NEW
      // edit whose responses cannot be stored has not happened, and must
      // surface as a failure rather than survive memory-only.
      await this.infoStore.applySideEffects(result.sideEffects.effects);
    } catch (error) {
      // The edit is durable but its side effects are not. Undo the queue entry
      // so the edit is not replayed on the next launch, and report the failure
      // rather than throw (see above).
      this.currentUnconfirmedAgg = previousUnconfirmedAgg;

      try {
        await this.db.unconfirmedActions2
          .where("[aggregateId+actionId]")
          .equals([this.aggregateId, actionMessage.id])
          .delete();
      } catch {
        // Best-effort: the replay would at worst re-apply this same edit.
      }

      return error instanceof Error ? error : new Error(String(error));
    }

    return null;
  }

  async getAction(
    actionId: string,
  ): Promise<IncomingUnconfirmedActionMessage | null> {
    const action = await this.db.unconfirmedActions2.get([
      this.aggregateId,
      actionId,
    ]);

    if (action) {
      return action.actionMessage;
    }

    for (const action of this.confirmedActionsSinceLoad) {
      if (action.id === actionId) {
        return action;
      }
    }

    return null;
  }

  async getAggregateAtVersion(
    reducer: Reducer,
    version: number,
  ): Promise<RoundAggregate | null> {
    let lastAggregatePriorToAction: RoundAggregate | null = null;

    if (this.confirmedSnapshotsSinceLoad.length > 0) {
      for (let i = 0; i < this.confirmedSnapshotsSinceLoad.length; i++) {
        const snapshot = this.confirmedSnapshotsSinceLoad[i];

        if (!snapshot) {
          continue;
        }

        if (snapshot.version <= version) {
          lastAggregatePriorToAction = snapshot;
        } else if (snapshot.version > version) {
          break;
        }
      }
    }

    const aggregateVersion = lastAggregatePriorToAction?.version ?? -1;

    if (aggregateVersion === -1) {
      return null;
    }

    let aggregate = lastAggregatePriorToAction;

    if (aggregateVersion < version) {
      const replayStart = this.confirmedActionsSinceLoad.findIndex(
        (action) => action.version === aggregateVersion + 1,
      );

      if (replayStart === -1) {
        throw new Error("Could not find replay start");
      }

      for (
        let i = replayStart;
        i < this.confirmedActionsSinceLoad.length;
        i++
      ) {
        const action = this.confirmedActionsSinceLoad[i];

        if (!action) {
          continue;
        }

        const result = reducer.performAction(aggregate, action);

        if (result instanceof Error) {
          throw result;
        }

        await applySideEffectsBestEffort(
          this.infoStore,
          this.fieldWriteSuppressedActionIds.has(action.id)
            ? withoutFieldResponseWrites(result.sideEffects.effects)
            : result.sideEffects.effects,
        );
        aggregate = result.agg;

        if (action.version === version) {
          break;
        }
      }
    }

    return aggregate
      ? anonymizeAggregate(
          this.viewerTrack,
          aggregate,
          this.viewerUserId ?? undefined,
        )
      : null;
  }

  performConfirmedAction(
    reducer: Reducer,
    actionMessage: IncomingActionMessage,
  ): Promise<Error | ActionError[]> {
    // Chain each action to run after the previous one completes
    this.confirmActionQueue = this.confirmActionQueue.then(
      () => this._performConfirmedActionInternal(reducer, actionMessage),
      // also run on rejection (if previous action errored)
      () => this._performConfirmedActionInternal(reducer, actionMessage),
    );
    return this.confirmActionQueue;
  }

  // Whether the confirmed action is exactly our own optimistic head, applied
  // to the same base, with the same reducer inputs — in which case
  // currentUnconfirmedAgg is already the confirmed state and can be adopted
  // without re-running the reducer, for ANY action type: application is a
  // pure function of (round, payload, meta), so an identical payload applied
  // to the same base provably yields an identical aggregate (see applyAction
  // in core.ts — timestamped types carry their creation time IN the payload).
  //
  // The ENVELOPE timestamp is deliberately not compared: the server assigns
  // its own, so it always differs — and the reducer never reads it. Every
  // envelope field the reducer does consume (payload, version, id, actors,
  // the admin bypass flag) must match; undoes/redoes are metadata the reducer
  // never reads.
  private canAdoptOptimisticHead(
    actionMessage: IncomingActionMessage,
    queuedOptimistic: UnconfirmedActionRecord | undefined,
  ): boolean {
    if (queuedOptimistic == null) {
      return false;
    }

    if (actionMessage.ignoredReason != null) {
      return false;
    }

    // The optimistic head must be exactly this action: last applied, at the
    // same version the server assigned (the version guards above already
    // ensured contiguity with lastConfirmedAgg).
    if (this.currentUnconfirmedAgg.latestActionId !== actionMessage.id) {
      return false;
    }

    if (this.currentUnconfirmedAgg.version !== actionMessage.version) {
      return false;
    }

    // Slow path keeps its every-100-versions snapshot bookkeeping.
    if (actionMessage.version % 100 === 0) {
      return false;
    }

    const optimistic = queuedOptimistic.actionMessage;
    return (
      optimistic.actingAsUserId === actionMessage.actingAsUserId &&
      optimistic.underlyingUserId === actionMessage.underlyingUserId &&
      (optimistic.createdBySfcAdmin ?? false) ===
        (actionMessage.createdBySfcAdmin ?? false) &&
      JSON.stringify(optimistic.payload) ===
        JSON.stringify(actionMessage.payload)
    );
  }
  private async _performConfirmedActionInternal(
    reducer: Reducer,
    actionMessage: IncomingActionMessage,
  ): Promise<Error | ActionError[]> {
    // Confirmed actions arrive from the server, so a local storage fault must
    // never escape as a throw: the caller chain (`performConfirmedAction` →
    // wrapper → `Client`) handles a RETURNED Error (Sentry + user notice +
    // keep processing) but a rejection used to jam the Client's action pump.
    // Timing is recorded for the OUTERMOST apply only: a version gap makes
    // `_applyConfirmedAction` re-enter this method for each missing action,
    // and those nested applies are part of the outer message's duration, not
    // separate records. Stamp a local reference, never the shared slot, so a
    // nested call can neither replace nor be finished by the outer one.
    const apply = {
      startedAt: Date.now(),
      endedAt: null as number | null,
      version: actionMessage.version,
    };
    const outermost =
      this.lastConfirmedApply == null ||
      this.lastConfirmedApply.endedAt != null;

    if (outermost) {
      this.lastConfirmedApply = apply;
    }

    try {
      return await this._applyConfirmedAction(reducer, actionMessage);
    } catch (error) {
      return error instanceof Error ? error : new Error(String(error));
    } finally {
      if (outermost) {
        apply.endedAt = Date.now();
      }
    }
  }

  private async _applyConfirmedAction(
    reducer: Reducer,
    actionMessage: IncomingActionMessage,
  ): Promise<Error | ActionError[]> {
    // Read the optimistic twin (if we performed this action locally) BEFORE
    // deleting it — the adoption fast path below compares against it.
    // Best-effort: this read exists only for the optimization, so a storage
    // fault here must not fail the confirmation — it just disables adoption
    // and the normal path (whose own hoisted read fails loudly if IndexedDB
    // is truly broken) proceeds.
    let queuedOptimistic: UnconfirmedActionRecord | undefined;

    try {
      queuedOptimistic = await this.db.unconfirmedActions2.get([
        this.aggregateId,
        actionMessage.id,
      ]);
    } catch {
      queuedOptimistic = undefined;
    }

    // Always attempt to remove a matching unconfirmed entry for the confirmed
    // action ID, even if this action was already applied to or is older than
    // the confirmed aggregate (e.g., after reconnection or duplicate delivery).
    // This keeps IndexedDB free of stale unconfirmed actions. Best-effort: a
    // failed delete leaves a stale row that the next confirmation retries; it
    // must not block applying the server's action.
    try {
      await this.db.unconfirmedActions2
        .where("[aggregateId+actionId]")
        .equals([this.aggregateId, actionMessage.id])
        .delete();
    } catch {
      // Retried at the post-apply delete below and on the next confirmation.
    }

    // Ignore any action that is not strictly newer than our last confirmed
    // aggregate. We may receive older confirmations after reconnection. The
    // duplicate delivery still PROVES this id is confirmed — record it, so a
    // stale queue row for it (a delete that failed, possibly in a previous
    // session) is filtered out of every later replay.
    if (actionMessage.version <= this.lastConfirmedAgg.version) {
      this.confirmedActionIdsSinceLoad.add(actionMessage.id);
      return [];
    }

    // Need to download actions from lastConfirmedAgg.version + 1 to
    // actionMessage.version - 1 before proceeding
    if (actionMessage.version > this.lastConfirmedAgg.version + 1) {
      const missingActionNumbers = Array.from(
        { length: actionMessage.version - this.lastConfirmedAgg.version - 1 },
        (_, i) => this.lastConfirmedAgg.version + i + 1,
      );
      const missingActions = await this.downloadActionsByVersion(
        this.aggregateId,
        missingActionNumbers,
      );

      const missingFromMissingActions: number[] = [];

      for (const actionNumber of missingActionNumbers) {
        if (missingActions[actionNumber] === undefined) {
          missingFromMissingActions.push(actionNumber);
        }
      }

      if (missingFromMissingActions.length > 0) {
        const missingActionsMessage = `Missing actions for aggregate ${this.aggregateId}: ${missingFromMissingActions.join(
          ", ",
        )}`;
        captureMessage(missingActionsMessage, {
          level: "warning",
        });
        return new Error(missingActionsMessage);
      }

      for (const actionNumber of missingActionNumbers) {
        const missingAction = missingActions[actionNumber];

        if (!missingAction) {
          continue;
        }

        // Call internal method directly to avoid re-acquiring the mutex. A
        // storage fault applying a gap action means the current action can't
        // apply either (it builds on the gap) — propagate it as the result.
        const applied = await this._performConfirmedActionInternal(
          reducer,
          missingAction,
        );

        if (applied instanceof Error) {
          return applied;
        }
      }
    }

    // Adoption fast path: when the server confirms exactly the action we
    // applied optimistically — same payload and reducer-relevant envelope,
    // contiguous version, nothing else unconfirmed — and the action type is
    // timestamp-independent, the optimistic aggregate IS the confirmed state.
    // Adopt it instead of re-running the reducer: identical content, and the
    // UI keeps the very object it already rendered (no re-render, warm
    // caches), skipping the re-apply and anonymize passes entirely. Anything
    // that fails a gate falls through to the normal re-apply path below.
    if (this.canAdoptOptimisticHead(actionMessage, queuedOptimistic)) {
      // Best-effort like the read above: a storage fault only disables the
      // optimization. A failed pre-apply delete also lands here as a nonzero
      // count, so adoption never proceeds with a stale row still queued.
      let remainingUnconfirmed = -1;

      try {
        remainingUnconfirmed = await this.db.unconfirmedActions2
          .where("aggregateId")
          .equals(this.aggregateId)
          .count();
      } catch {
        // Fall through to the normal path.
      }

      if (remainingUnconfirmed === 0) {
        // Anonymization is a viewer-dependent projection the reducer doesn't
        // apply: this viewer's own action can tighten what they may see
        // (deanonymize off, a track move, notes visibility), so project the
        // adopted aggregate exactly like the slow path does. Mutative returns
        // the SAME object when nothing needs redacting — the overwhelmingly
        // common case — so aggregate identity (and every identity-keyed
        // cache) is preserved; when the action did change the projection, the
        // redacted copy correctly replaces the rendered one.
        const adopted = anonymizeAggregate(
          this.viewerTrack,
          this.currentUnconfirmedAgg,
          this.viewerUserId ?? undefined,
        );
        this.currentUnconfirmedAgg = adopted;
        this.lastConfirmedAgg = adopted;
        this.lastConfirmedAggAt = Date.now();
        this.confirmedActionsSinceLoad.push(actionMessage);
        this.confirmedActionIdsSinceLoad.add(actionMessage.id);
        // Side effects already ran during the optimistic apply; the aggregate
        // cache write-through still happens so re-init stays current.
        this.scheduleConfirmedAggregateCachePersist();
        return [];
      }
    }

    // Read the unconfirmed queue BEFORE anything advances. This is the one
    // remaining fallible storage read in the apply, so hoisting it means a
    // runtime IndexedDB failure surfaces while `lastConfirmedAgg` still holds
    // the OLD version — the returned Error then leaves nothing half-applied,
    // and the acknowledgement `Client` sends afterwards reports the old
    // version, so the server re-delivers this confirmation rather than
    // treating a half-applied one as received.
    const unconfirmedActions = sortUnconfirmedActions(
      await this.db.unconfirmedActions2
        .where("aggregateId")
        .equals(this.aggregateId)
        .toArray(),
    ).map((action) => action.actionMessage);

    // A confirmed action's before-version must hydrate before the reducer
    // runs (this path is online by definition, so the loader's network tier
    // is available). When it is unavailable — e.g. a transient HTTP failure
    // during a socket reconnect — do NOT apply onto incomplete responses:
    // return an Error while `lastConfirmedAgg` still holds the old version,
    // exactly like the hoisted storage read above, so nothing is
    // half-applied and the acknowledgement reports the old version — the
    // server re-delivers this confirmation and the hydration retries.
    // Two exemptions, both because deferring would wedge the stream forever
    // (the server re-delivers the same confirmation indefinitely, blocking
    // every later one): ignored confirmations, whose payload the reducer
    // skips — the server rejected that very edit; and a before-version the
    // server AUTHORITATIVELY lacks, which re-delivery can never produce —
    // the server already confirmed the action by applying the reducer's own
    // newest-responses fallback, so applying here converges with it.
    if (actionMessage.ignoredReason == null) {
      const hydration = await hydrateFieldResponseDependency(
        this.infoStore,
        actionMessage.payload,
      );

      if (hydration === "unavailable") {
        const missMessage = `Confirmed action ${actionMessage.id} deferred: its before-version could not be hydrated; awaiting re-delivery`;
        captureMessage(missMessage, { level: "warning" });
        return new Error(missMessage);
      }

      if (hydration === "absent") {
        // The server confirmed this edit without its before-version (its
        // reducer merged onto the server's own newest responses) — and the
        // after-version it wrote IS the authoritative result. Adopt that as
        // this client's newest so the reducer's fallback below reproduces it
        // exactly, instead of merging onto whatever happens to be hydrated
        // here (possibly nothing, mid deferred hydration).
        const fieldChange = actionMessage.payload as {
          proposalId?: string;
          afterFieldResponsesId?: string;
        };
        const { proposalId, afterFieldResponsesId } = fieldChange;
        let adoptedAfterVersion = false;

        if (proposalId != null && afterFieldResponsesId != null) {
          const afterProbe = await probeFieldResponseVersion(
            this.infoStore,
            proposalId,
            afterFieldResponsesId,
          );

          if (afterProbe === "unavailable") {
            // Retryable: the server wrote this blob when it confirmed.
            const missMessage = `Confirmed action ${actionMessage.id} deferred: its after-version could not be hydrated; awaiting re-delivery`;
            captureMessage(missMessage, { level: "warning" });
            return new Error(missMessage);
          }

          if (afterProbe === "ready") {
            const after = this.infoStore.readFieldResponsesFromCache(
              proposalId,
              afterFieldResponsesId,
            );

            if (after != null) {
              await applySideEffectsBestEffort(this.infoStore, [
                {
                  type: "WRITE_FIELD_RESPONSES",
                  roundId: this.aggregateId,
                  proposalId,
                  fieldResponses: after,
                },
              ]);
              adoptedAfterVersion = true;
            }
          }
        }

        if (!adoptedAfterVersion) {
          // Terminal: neither end of the edit exists on the server (when the
          // server's own reducer fell back, it persisted its result under a
          // recomputed hash the confirmation does not carry, while the
          // aggregate keeps the claimed id — deterministically, on every
          // replica). Any merge computed HERE is unauthoritative —
          // persisting it would fabricate confirmed-looking content under
          // fresh hashes that later edits chain onto. Advance the
          // confirmation but drop its field-response write. The proposal's
          // text stays stale until a later edit re-points the aggregate:
          // current-responses fetches key on the aggregate pointer, so no
          // client can address the server's orphaned row — a server-side
          // limitation this adapter cannot heal, only refuse to make worse.
          this.fieldWriteSuppressedActionIds.add(actionMessage.id);
          captureMessage(
            `Confirmed action ${actionMessage.id} applied without a field-response write: neither its before- nor after-version exists on the server`,
            { level: "warning" },
          );
        }
      }
    }

    const result = reducer.performAction(this.lastConfirmedAgg, actionMessage);

    if (result instanceof Error) {
      return result;
    }

    // Note that we could optimize this by only doing it if you're in a track or
    // if the action tightened access control (i.e. it was
    // ROUND_MEMBER_PROPERTY_CHANGE with feedbackVisibleByDefault set to false
    // or NOTES_AND_FEEDBACK_VISIBILITY_CHANGE with visibility set to hidden or
    // default).
    this.lastConfirmedAgg = anonymizeAggregate(
      this.viewerTrack,
      result.agg,
      this.viewerUserId ?? undefined,
    );
    this.lastConfirmedAggAt = Date.now();
    this.confirmedActionsSinceLoad.push(actionMessage);
    this.confirmedActionIdsSinceLoad.add(actionMessage.id);
    await applySideEffectsBestEffort(
      this.infoStore,
      this.fieldWriteSuppressedActionIds.has(actionMessage.id)
        ? withoutFieldResponseWrites(result.sideEffects.effects)
        : result.sideEffects.effects,
    );

    if (result.agg.version % 100 === 0) {
      this.confirmedSnapshotsSinceLoad.push(result.agg);
    }

    const errors: ActionError[] = [];
    let currentUnconfirmedAgg: RoundAggregate = this.lastConfirmedAgg;

    for (const unconfirmedAction of unconfirmedActions) {
      if (this.confirmedActionIdsSinceLoad.has(unconfirmedAction.id)) {
        // Already confirmed — replaying it on an aggregate that contains it
        // would double-apply the edit. Reachable when a best-effort queue
        // delete failed, so sweep the stale row again here (best-effort too).
        if (unconfirmedAction.id !== actionMessage.id) {
          void this.db.unconfirmedActions2
            .where("[aggregateId+actionId]")
            .equals([this.aggregateId, unconfirmedAction.id])
            .delete()
            .catch(() => {});
        }

        continue;
      }

      // Same rule as every other apply path: no complete before-version, no
      // local apply — the row stays queued for the server. Without this, an
      // edit init deliberately left unapplied would be re-applied here onto
      // empty context by the next unrelated confirmation.
      if (
        (await hydrateFieldResponseDependency(
          this.infoStore,
          unconfirmedAction.payload,
        )) !== "ready"
      ) {
        continue;
      }

      const result = reducer.performAction(
        currentUnconfirmedAgg,
        unconfirmedAction,
      );

      if (result instanceof Error) {
        errors.push({ actionId: unconfirmedAction.id, error: result });
      } else {
        currentUnconfirmedAgg = result.agg;
        await applySideEffectsBestEffort(
          this.infoStore,
          result.sideEffects.effects,
        );
      }
    }

    this.currentUnconfirmedAgg = currentUnconfirmedAgg;

    // Best-effort, same as the pre-apply delete above: a stale row is retried
    // on the next confirmation and skipped by id during replay.
    try {
      await this.db.unconfirmedActions2
        .where("[aggregateId+actionId]")
        .equals([this.aggregateId, actionMessage.id])
        .delete();
    } catch {
      // Cleaned up by the next confirmation's pre-apply delete.
    }

    // Keep the offline cache current to the last confirmed action. Write-through
    // (kicks off immediately, no timer) so a re-init after navigation/refresh
    // never serves a snapshot older than the last applied confirmation. Bursts
    // (live stream or reconnect catch-up) coalesce into ~one trailing write via
    // the in-flight guard, so we don't write once per action.
    this.scheduleConfirmedAggregateCachePersist();

    return errors;
  }

  /**
   * The persist that follows a confirmed action. Deferred while the tab is
   * hidden (see `OfflineCacheConfig.visibility`): the deferred write lands
   * when the tab becomes visible, even if no further action arrives. Only the
   * two confirmed-action paths route through here — the fresh-download write
   * and explicit flushes go straight to `scheduleAggregateCachePersist`.
   */
  private scheduleConfirmedAggregateCachePersist(): void {
    if (
      this.offlineCacheEnabled &&
      this.visibility != null &&
      this.visibility.isHidden()
    ) {
      this.aggregatePersistDeferred = true;
      this.unsubscribeVisibility ??= this.visibility.onChange(() => {
        if (this.visibility?.isHidden() !== false) {
          return;
        }

        this.unsubscribeVisibility?.();
        this.unsubscribeVisibility = null;

        if (!this.aggregatePersistDeferred) {
          return;
        }

        this.scheduleAggregateCachePersist();
      });
      return;
    }

    this.scheduleAggregateCachePersist();
  }

  private scheduleAggregateCachePersist(): void {
    // Gated to the iPad shell — desktop/web and iPhone never touch the cache
    // table. Centralized here so every caller (confirmed-action hook, flush,
    // teardown) is a no-op when the cache is disabled.
    if (!this.offlineCacheEnabled) {
      return;
    }

    this.aggregatePersistDirty = true;

    if (this.aggregatePersistPromise == null) {
      this.aggregatePersistPromise = this.drainAggregateCachePersist();
    }
  }

  private async drainAggregateCachePersist(): Promise<void> {
    try {
      while (this.aggregatePersistDirty) {
        this.aggregatePersistDirty = false;
        // Reads this.lastConfirmedAgg at write time, so a write that started
        // before later actions still ends up persisting the newest state on
        // its final loop iteration — which is also why any deferred
        // hidden-tab persist is satisfied by this write.
        this.aggregatePersistDeferred = false;
        const persist = {
          startedAt: Date.now(),
          endedAt: null as number | null,
          version: this.lastConfirmedAgg.version,
        };
        this.lastCachePersist = persist;
        await writeCachedAggregate(
          this.db.roundAggregateCache,
          {
            aggregateId: this.aggregateId,
            schemaHash,
            userProfileHash: this.userProfileHash,
            viewerTrack: this.viewerTrack,
            viewerUserId: this.viewerUserId,
          },
          this.lastConfirmedAgg,
          {
            fromFreshDownload:
              this.lastConfirmedAgg.version === this.downloadedVersion,
            snapshotAt: this.lastConfirmedAggAt,
            logRevision: this.cacheLogRevision,
          },
        );
        persist.endedAt = Date.now();
      }
    } finally {
      this.aggregatePersistPromise = null;
    }
  }

  /** Storage-side facts for a message-processing stall report (SP-2823). */
  getStallDiagnostics(): Record<string, unknown> {
    const now = Date.now();
    const describe = (
      op: { startedAt: number; endedAt: number | null; version: number } | null,
    ) =>
      op == null
        ? null
        : {
            version: op.version,
            inFlight: op.endedAt == null,
            durationMs: (op.endedAt ?? now) - op.startedAt,
            agoMs: now - (op.endedAt ?? op.startedAt),
          };
    return {
      lastConfirmedApply: describe(this.lastConfirmedApply),
      lastCachePersist: describe(this.lastCachePersist),
      cachePersistPending: this.aggregatePersistDirty,
      cachePersistDeferred: this.aggregatePersistDeferred,
      confirmedSnapshotAgeMs: now - this.lastConfirmedAggAt,
      tabHidden: this.visibility?.isHidden() ?? null,
      offlineCacheEnabled: this.offlineCacheEnabled,
    };
  }

  /** Awaits any in-flight/pending offline-cache persist (used by tests + teardown). */
  async flushAggregateCachePersist(): Promise<void> {
    this.scheduleAggregateCachePersist();

    while (this.aggregatePersistPromise != null) {
      await this.aggregatePersistPromise;
    }
  }

  /**
   * Reads offline-cache metadata for a round without constructing an adapter.
   * Used by surfaces (e.g. the homepage, video-download affordances) that want
   * to show what's available offline before opening the round.
   */
  static async readCachedAggregateMeta(aggregateId: string): Promise<{
    cachedAt: number;
    version: number;
  } | null> {
    try {
      const db = getDb();
      const record = await db.roundAggregateCache.get(aggregateId);

      if (record == null) {
        return null;
      }

      return { cachedAt: record.cachedAt, version: record.version };
    } catch {
      return null;
    }
  }

  // Check:
  // 1. The version of the last confirmed action (N_a) is less than or equal to
  //    the last confirmed aggregate (N_d): N_a <= N_d
  // 2. getUnconfirmedAggregateSync().version (N_u): N_u >= N_a
  // 3. No unconfirmed actions with the same id as a confirmed action
  async checkConsistency(): Promise<void> {
    const confirmedActionsSinceLoad = this.confirmedActionsSinceLoad;
    const lastConfirmedAction =
      confirmedActionsSinceLoad.length > 0
        ? confirmedActionsSinceLoad[confirmedActionsSinceLoad.length - 1]
        : null;

    const unconfirmedActions = await this.db.unconfirmedActions2
      .where("aggregateId")
      .equals(this.aggregateId)
      .toArray();

    const n_a = lastConfirmedAction?.version ?? -1;
    const n_d = this.lastConfirmedAgg.version;

    if (n_a > n_d) {
      throw new Error(
        `lastConfirmedAction.version (${n_a}) > this.lastConfirmedAgg.version (${n_d})`,
      );
    }

    if (this.getUnconfirmedAggregateSync().version < n_a) {
      throw new Error(
        `getUnconfirmedAggregateSync().version (${this.getUnconfirmedAggregateSync().version}) < n_a (${n_a})`,
      );
    }

    const confirmedActionIds = new Set(
      confirmedActionsSinceLoad.map((action) => action.id),
    );

    for (const unconfirmedAction of unconfirmedActions) {
      if (confirmedActionIds.has(unconfirmedAction.actionId)) {
        throw new Error(
          `unconfirmed action ${unconfirmedAction.actionId} has the same id as a confirmed action`,
        );
      }
    }
  }

  async clearUnconfirmedActions(): Promise<void> {
    await this.db.unconfirmedActions2
      .where("aggregateId")
      .equals(this.aggregateId)
      .delete();
    this.currentUnconfirmedAgg = this.lastConfirmedAgg;
  }

  async removeUnconfirmedAction(
    reducer: Reducer,
    actionId: string,
  ): Promise<void> {
    await this.db.unconfirmedActions2
      .where("[aggregateId+actionId]")
      .equals([this.aggregateId, actionId])
      .delete();

    const unconfirmedActions = sortUnconfirmedActions(
      await this.db.unconfirmedActions2
        .where("aggregateId")
        .equals(this.aggregateId)
        .toArray(),
    ).map((action) => action.actionMessage);

    let currentUnconfirmedAgg: RoundAggregate = this.lastConfirmedAgg;

    for (const unconfirmedAction of unconfirmedActions) {
      // Same rule as the initial replay: no complete before-version, no
      // local apply — the row stays queued for the server.
      if (
        (await hydrateFieldResponseDependency(
          this.infoStore,
          unconfirmedAction.payload,
        )) !== "ready"
      ) {
        continue;
      }

      const result = reducer.performAction(
        currentUnconfirmedAgg,
        unconfirmedAction,
      );

      if (result instanceof Error) {
        continue;
      }

      await applySideEffectsBestEffort(
        this.infoStore,
        result.sideEffects.effects,
      );
      currentUnconfirmedAgg = result.agg;
    }

    this.currentUnconfirmedAgg = currentUnconfirmedAgg;
  }

  getConfirmedActions(): IncomingActionMessage[] {
    return this.confirmedActionsSinceLoad;
  }

  async getUnconfirmedActions(): Promise<
    {
      createdAt: number;
      message: IncomingUnconfirmedActionMessage;
    }[]
  > {
    return sortUnconfirmedActions(
      await this.db.unconfirmedActions2
        .where("aggregateId")
        .equals(this.aggregateId)
        .toArray(),
    ).map(({ actionMessage, createdAt }) => ({
      message: actionMessage,
      createdAt,
    }));
  }

  // Field-response versions the queued edits need as replay context. The
  // aggregate stops referencing an edit's before-version once replay points
  // the proposal at its after-version, so cache eviction keyed on aggregate
  // references alone would delete what a later replay must hydrate. The
  // queue and the field-response cache are both global across rounds, so
  // this reads EVERY round's queued rows — a sweep while one round is open
  // must not strand another round's queued edits.
  async getQueuedFieldResponseDependencies(): Promise<string[]> {
    const versions: string[] = [];

    for (const row of await this.db.unconfirmedActions2.toArray()) {
      const payload = row.actionMessage.payload;

      if (payload.type !== "PROPOSAL_FIELD_RESPONSE_CHANGE") {
        continue;
      }

      if (payload.beforeFieldResponsesId != null) {
        versions.push(payload.beforeFieldResponsesId);
      }

      versions.push(payload.afterFieldResponsesId);
    }

    return versions;
  }

  getUnconfirmedAggregateSync(): RoundAggregate {
    return this.currentUnconfirmedAgg;
  }

  getConfirmedAggregateSync(): RoundAggregate {
    return this.lastConfirmedAgg;
  }

  getLastConfirmedVersion(): number {
    return this.lastConfirmedAgg.version;
  }

  async getAggregateBeforeUnconfirmedAction(
    reducer: Reducer,
    actionId: string,
  ): Promise<RoundAggregate | Error> {
    const actions = await this.getUnconfirmedActions();
    let agg = this.lastConfirmedAgg;

    for (const action of actions) {
      if (action.message.id === actionId) {
        return agg;
      }

      // Same rule as every replay path: an unhydratable field edit was never
      // applied to the live aggregate, so the undo baseline must skip it too
      // — applying it here would both diverge from what the user sees and
      // persist an incomplete response set.
      if (
        (await hydrateFieldResponseDependency(
          this.infoStore,
          action.message.payload,
        )) !== "ready"
      ) {
        continue;
      }

      const result = reducer.performAction(agg, action.message);

      if (result instanceof Error) {
        return result;
      }

      await applySideEffectsBestEffort(
        this.infoStore,
        result.sideEffects.effects,
      );
      agg = result.agg;
    }

    return agg;
  }
}

// Whether the stored metadata means this open is an account or schema change,
// so the cached round + queue (and, separately, the proposal text) must be
// wiped rather than trusted.
//
// A schema bump invalidates the whole DB. Do NOT wipe on a profile/track/
// impersonation change: `SPAStorage` is a single global DB shared across rounds,
// so a blanket clear would (a) nuke OTHER rounds' valid cached aggregates, and
// (b) — worse — delete OTHER rounds' queued offline edits before they've synced.
// Those are safe to keep across a profile change: aggregate rows are re-validated
// per-read against schemaHash + userProfileHash + viewerTrack.
//
// An OWNER change is different: a new underlying account is using this device
// without signing out (cookie expiry + relogin, or a native token swap — the
// sign-out clear never ran). The previous user's queued offline edits must NOT
// survive — `Server.performAction` resolves the underlying user from the live
// session and an admin can honor a queued `actingAsUserId`, so a later session
// could submit the prior user's edits while impersonating that actor. A row that
// predates this field (metadata exists but `ownerUserId` is null) can't be
// attributed to the current user, so it's a mismatch too. Only enforced when the
// caller supplies an owner, and never on a fresh DB (`metadata == null`).
// Why the DB-level wipe ran (null = no wipe). "fresh_db" is the no-metadata
// case: the wipe formally runs but nothing existed to lose, so a subsequent
// cache miss stays attributed to the empty device, not to a change. The other
// two are what the round-open signal reports when the wipe is what emptied the
// cache — validateDb clears roundAggregateCache BEFORE the cache-first read,
// so without this the per-read schema check can never fire and a deploy-wide
// invalidation would masquerade as a fleet of fresh devices ("no_row").
type AccountWipeReason = "fresh_db" | "schema_changed" | "account_changed";

function accountWipeReason(
  metadata: { schemaHash?: string; ownerUserId?: string | null } | undefined,
  ownerUserId?: string,
): AccountWipeReason | null {
  if (metadata == null) {
    return "fresh_db";
  }

  // Owner change is checked FIRST: the two reasons clear different scopes
  // (schema spares the queued edits, owner does not), and a simultaneous
  // schema move must not mask the stricter, privacy-clearing owner path.
  if (ownerUserId != null && metadata.ownerUserId !== ownerUserId) {
    return "account_changed";
  }

  if (metadata.schemaHash !== schemaHash) {
    return "schema_changed";
  }

  return null;
}

// Read-only peek, used by `init` BEFORE `validateDb` so it can durably clear the
// separate proposal-text store before the owner stamp advances.
async function peekWipeReason(
  db: Db,
  ownerUserId?: string,
): Promise<AccountWipeReason | null> {
  return accountWipeReason(await db.metadata.get(["singleton"]), ownerUserId);
}

// The reducer reads a field edit's before-version through the SYNCHRONOUS
// cache readers, so every apply path must hydrate that version first
// (memory ← IndexedDB ← network). "unavailable" means it could not be
// produced right now and a retry can succeed; "absent" means the server
// authoritatively lacks it, so waiting on it can never end. A store without
// the probe cannot tell the two apart and reports the safe "unavailable".
async function hydrateFieldResponseDependency(
  infoStore: InfoStoreReader,
  payload: unknown,
): Promise<"ready" | "absent" | "unavailable"> {
  const fieldChange = payload as {
    type?: string;
    proposalId?: string;
    beforeFieldResponsesId?: string | null;
  };

  if (
    fieldChange.type !== "PROPOSAL_FIELD_RESPONSE_CHANGE" ||
    fieldChange.proposalId == null ||
    fieldChange.beforeFieldResponsesId == null
  ) {
    return "ready";
  }

  return probeFieldResponseVersion(
    infoStore,
    fieldChange.proposalId,
    fieldChange.beforeFieldResponsesId,
  );
}

/**
 * Side effects rewrite proposal text in `ClientInfoStore`, which updates its
 * in-memory cache and notifies subscribers BEFORE persisting to Dexie — so
 * when only the persist fails (disk full, IndexedDB backend lost: both seen
 * in production), the session's UI is already correct and the on-disk copy is
 * a rebuildable cache, re-fetched or re-replayed on the next load. Failing
 * the caller over it would wedge the confirmation pump (the server re-delivers
 * into the same failing write) or fail a round open. The ONE path that must
 * NOT use this is a new optimistic edit's first apply: an edit that cannot be
 * stored has not happened.
 */
async function applySideEffectsBestEffort(
  infoStore: ClientSideEffectWriter,
  effects: SideEffect[],
): Promise<void> {
  try {
    await infoStore.applySideEffects(effects);
  } catch (error) {
    captureMessage(
      `Side-effect persist failed; continuing with in-memory copy: ${String(error)}`,
      { level: "warning" },
    );
  }
}

function withoutFieldResponseWrites(effects: SideEffect[]): SideEffect[] {
  // The write arrives as the reducer's PATCH_FIELD_RESPONSE intent (the info
  // store turns it into WRITE_FIELD_RESPONSES when applying); drop both forms.
  return effects.filter(
    (effect) =>
      effect.type !== "WRITE_FIELD_RESPONSES" &&
      effect.type !== "PATCH_FIELD_RESPONSE",
  );
}

async function probeFieldResponseVersion(
  infoStore: InfoStoreReader,
  proposalId: string,
  version: string,
): Promise<"ready" | "absent" | "unavailable"> {
  try {
    const probe = (
      infoStore as {
        probeFieldResponsesAtVersion?: (
          proposalId: string,
          version: string,
        ) => Promise<{ status: "found" | "absent" | "unreachable" }>;
      }
    ).probeFieldResponsesAtVersion;

    if (probe) {
      const probed = await probe.call(infoStore, proposalId, version);

      if (probed.status === "found") {
        return "ready";
      }

      return probed.status === "absent" ? "absent" : "unavailable";
    }

    const hydrated = await infoStore.readFieldResponsesAtVersion(
      proposalId,
      version,
    );
    return hydrated == null ? "unavailable" : "ready";
  } catch {
    return "unavailable";
  }
}

async function validateDb(
  db: Db,
  userProfileHash: string,
  ownerUserId?: string,
): Promise<AccountWipeReason | null> {
  // Read-modify-write in ONE transaction: if a destructive clear below fails or
  // aborts, the metadata must NOT advance to the new identity — otherwise the
  // next init would see a match and skip the cleanup, leaving the previous
  // account's queued edits / cached aggregate in place. Clearing and recording
  // the new identity are therefore atomic (the put happens only if the clears
  // committed). Returns why a wipe ran (null when none did) so the caller can
  // attribute the now-empty cache on the round-open signal.
  return db.transaction(
    "rw",
    "metadata",
    "unconfirmedActions2",
    "roundAggregateCache",
    () =>
      db.metadata.get(["singleton"]).then((metadata) => {
        // Keep this as a Dexie promise chain instead of an `async` callback.
        // Dexie can commit a native IndexedDB transaction before a transpiled
        // async callback settles, which surfaces as PrematureCommitError.
        const writeMetadata = () =>
          db.metadata.put({
            id: "singleton",
            userProfileHash,
            schemaHash,
            ownerUserId: ownerUserId ?? metadata?.ownerUserId,
          });

        const wipeReason = accountWipeReason(metadata, ownerUserId);

        if (wipeReason == null) {
          return writeMetadata().then(() => null);
        }

        // What gets cleared depends on WHY. A schema change invalidates
        // only the rebuildable cache; the queued edits are unsynced user
        // work that exists nowhere else. Only an account change — or
        // unknown ownership — clears the queue too, for privacy.
        const clears =
          wipeReason === "schema_changed"
            ? db.roundAggregateCache.clear()
            : db.unconfirmedActions2
                .clear()
                .then(() => db.roundAggregateCache.clear());
        return clears.then(writeMetadata).then(() => wipeReason);
      }),
  );
}
